'use client'

import React from 'react'
import { Container, Flex, FlexItem } from '@quark/layout'
import { Carousel } from '@quark/carousel'
import { Text } from '@quark/typography'
import { Avatar } from '@quark/avatar'
import { Reveal } from '@quark/animation'
import RatingStars from '@components/CustomerStory/RatingStars/RatingStars'
import { BrandVars } from '@quark/theme'
import { TrustProps } from '@interfaces/home'
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import { NewHomePageComponent } from '@components/HomePage/utils/HomeComponentType'
import PropertyControlledComponent from '@components/PropertyControlledComponent/PropertyControlledComponent'
import { Button } from '@quark/button'
import TrustHomeVideoPlayers from '@components/Home/VideoPlayer'

import styles from './trust.module.scss'

const getCarouselContent = stories =>
  stories?.map((story, index) => ({
    content: (
      <Container
        key={index}
        textAlign="center"
        px={{ sm: 'spacing-40', md: 'spacing-112', lg: 'spacing-80' }}
        paddingTop={{ sm: 'spacing-32', md: 'spacing-32', lg: 'spacing-0' }}>
        <Flex gap="spacing-32" flexDirection="column">
          <Flex alignItems="center" gap="spacing-16" flexDirection="column">
            <RatingStars rating={story?.rating} total={5} />
            <Text as="div" fontStyle="um_text-base/normal" color="base/primary">
              {story?.description}
            </Text>
          </Flex>
          <Flex
            flexDirection="row"
            gap={{ sm: 'spacing-8', md: 'spacing-16', lg: 'spacing-16' }}
            size="fullWidth"
            alignItems="center"
            justifyContent="center">
            <Avatar size={{ sm: 'md', md: 'md', lg: 'md' }} type="circle" backgroundColor="gray/200">
              <Text fontStyle="um_text-xl/normal" color="base/primary">
                {story?.customerName.charAt(0).toUpperCase()}
              </Text>
            </Avatar>
            <Flex flexDirection="column">
              <Text textAlign="left" fontStyle="um_text-base/normal" color="base/primary">
                {story?.customerName}
              </Text>
              <Text textAlign="left" fontStyle="um_text-base/normal" color="base/secondary">
                {story?.customerCompany}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </Container>
    ),
  }))

const Trust = React.memo((data: TrustProps) => {
  const videoPlayerDataArray = Array.isArray(data?.videoPlayerData)
    ? data.videoPlayerData
    : data?.videoPlayerData
      ? [data.videoPlayerData]
      : []
  const isMultipleVideos = videoPlayerDataArray?.length > 1

  const handleViewMoreClick = async (eventProperties: {
    eventName?: string
    customProperties?: Record<string, any>
  }) => {
    await trackEvent(eventProperties?.eventName ?? 'tap_btn_interaction', {
      page_type: 'acko_home_page',
      ...eventProperties?.customProperties,
    })
    return window.open(data?.carouselCardData?.viewMore?.url, '_blank')
  }

  const type = isMultipleVideos ? 'fluid' : 'root'

  return (
    <Container id={NewHomePageComponent.Trust} backgroundColor="base/overlay" type="fluid">
      <Container
        type={type}
        py={{ lg: 'spacing-80', md: 'spacing-56', sm: 'spacing-40' }}
        px={
          isMultipleVideos
            ? { sm: 'spacing-0', md: 'spacing-0', lg: 'spacing-0' }
            : { sm: 'spacing-16', md: 'spacing-32', lg: 'spacing-80' }
        }>
        <Flex gap={{ lg: 'spacing-64', md: 'spacing-40', sm: 'spacing-32' }} flexDirection="column">
          <Flex gap={{ lg: 'spacing-32', md: 'spacing-24', sm: 'spacing-32' }} flexDirection="column">
            <Reveal as="div" animationProps={{ threshold: 0.1, animationDelayMs: 500 }}>
              <Flex
                justifyContent="center"
                alignItems="center"
                gap={{ lg: 'spacing-32', md: 'spacing-16', sm: 'spacing-20' }}
                flexDirection="column"
                px={
                  !isMultipleVideos
                    ? { sm: 'spacing-0', md: 'spacing-0', lg: 'spacing-0' }
                    : { sm: 'spacing-16', md: 'spacing-32', lg: 'spacing-80' }
                }>
                <Text
                  as="span"
                  display={{ lg: 'block', md: 'block', sm: 'none' }}
                  fontStyle={{
                    sm: 'um_text-2xl/bold',
                    md: 'um_text-3xl/bold',
                    lg: isMultipleVideos ? 'um_text-5xl/bold' : 'um_text-4xl/bold',
                  }}
                  color="base/primary"
                  textAlign="center"
                  whiteSpace="pre">
                  <span dangerouslySetInnerHTML={{ __html: data?.heading || '' }} />
                </Text>
                <PropertyControlledComponent controllerProperty={data?.mobileHeading} fallback={null}>
                  <Text
                    as="span"
                    display={{ lg: 'none', md: 'none', sm: 'block' }}
                    fontStyle={{
                      sm: isMultipleVideos ? 'um_text-4xl/bold' : 'um_text-2xl/bold',
                      md: 'um_text-5xl/bold',
                      lg: 'um_text-6xl/bold',
                    }}
                    color="base/primary"
                    textAlign="center"
                    whiteSpace="pre">
                    <span dangerouslySetInnerHTML={{ __html: data?.mobileHeading || '' }} />
                  </Text>
                </PropertyControlledComponent>
                <Flex flexDirection="column" gap={{ lg: 'spacing-20', md: 'spacing-12', sm: 'spacing-16' }}>
                  <PropertyControlledComponent controllerProperty={data?.subHeading?.items?.length} fallback={null}>
                    <Flex
                      gap={{ sm: 'spacing-16', md: 'spacing-80', lg: 'spacing-80' }}
                      flexDirection="row"
                      px={
                        !isMultipleVideos
                          ? { sm: 'spacing-0', md: 'spacing-0', lg: 'spacing-0' }
                          : { sm: 'spacing-16', md: 'spacing-32', lg: 'spacing-80' }
                      }>
                      {data?.subHeading?.items?.map((item, idx) => (
                        <React.Fragment key={idx}>
                          <FlexItem alignSelf="stretch">
                            <Flex gap="spacing-8" flexDirection="column" alignItems="center" justifyContent="center">
                              <Text
                                as="span"
                                gradientColor="text"
                                bgColor="linear-gradient(180deg, var(--text-accent-textBrand-dark, #8B5CF6) 38.89%, var(--Surface-accent-Pink-pink-800, rgba(236, 72, 153, 0.72)) 151.85%)"
                                fontStyle={{ sm: 'um_text-2xl/bold', md: 'um_text-3xl/bold', lg: 'um_text-5xl/bold' }}
                                color="base/primary">
                                {item?.title}
                              </Text>
                              <Text
                                textAlign="center"
                                fontStyle={{
                                  sm: 'um_text-xs/normal',
                                  md: 'um_text-sm/normal',
                                  lg: 'um_text-lg/normal',
                                }}
                                whiteSpace={{ sm: 'pre', md: 'normal', lg: 'normal' }}
                                color="base/primary">
                                {item?.subTitle}
                              </Text>
                            </Flex>
                          </FlexItem>
                          {Array.isArray(data?.subHeading?.items) &&
                            idx !== (data?.subHeading?.items.length ?? 0) - 1 && (
                              <div
                                style={{
                                  background: `linear-gradient(180deg, ${BrandVars.Surface['base/overlay']} 10%, ${BrandVars.Surface['base/secondary']} 50%, ${BrandVars.Surface['base/overlay']} 90%)`,
                                  width: '1px',
                                }}>
                                &nbsp;
                              </div>
                            )}
                        </React.Fragment>
                      ))}
                    </Flex>
                  </PropertyControlledComponent>
                  <Text
                    fontStyle={{ lg: 'um_text-xs/normal', md: 'um_text-xs/normal', sm: 'um_text-2xs/normal' }}
                    color="base/secondary"
                    textAlign="center">
                    UID: 7993 | T&Cs Apply*
                  </Text>
                </Flex>
              </Flex>
            </Reveal>
            <Flex gap={{ lg: 'spacing-24', md: 'spacing-24', sm: 'spacing-16' }} flexDirection="column">
              <Reveal as="div" animationProps={{ threshold: 0.1, animationDelayMs: 500 }}>
                <Flex
                  justifyContent="center"
                  alignItems={{ sm: 'center', md: 'stretch', lg: 'stretch' }}
                  flexDirection={{ sm: 'column', md: 'column', lg: 'row' }}
                  gap={{ lg: 'spacing-24', md: 'spacing-24', sm: 'spacing-16' }}
                  px={
                    !isMultipleVideos
                      ? { sm: 'spacing-0', md: 'spacing-0', lg: 'spacing-0' }
                      : { sm: 'spacing-0', md: 'spacing-32', lg: 'spacing-80' }
                  }
                  size="fullWidth">
                  <PropertyControlledComponent controllerProperty={data?.videoCardData} fallback={null}>
                    <Container
                      minWidth={0}
                      backgroundColor="base/elevated"
                      textAlign="center"
                      borderRadius={{ lg: 'radius-6xl', md: 'radius-5xl', sm: 'radius-4xl' }}
                      paddingTop={{ lg: 'spacing-32', md: 'spacing-32', sm: 'spacing-20' }}
                      paddingLeft={{ lg: 'spacing-32', md: 'spacing-32', sm: 'spacing-20' }}
                      paddingRight={{ lg: 'spacing-32', md: 'spacing-32', sm: 'spacing-20' }}
                      overflow="hidden">
                      <Flex alignItems="center" justifyItems="center" gap="spacing-32" flexDirection="column">
                        <Text
                          fontStyle={{ sm: 'um_text-xl/bold', md: 'um_text-3xl/bold', lg: 'um_text-3xl/bold' }}
                          color="base/primary">
                          {data?.videoCardData?.title}
                        </Text>
                        <div className={styles.mobile_frame}>
                          <video src={data?.videoCardData?.videoSrc} autoPlay muted loop playsInline />
                        </div>
                      </Flex>
                    </Container>
                  </PropertyControlledComponent>
                  <PropertyControlledComponent controllerProperty={data?.carouselCardData} fallback={null}>
                    <Container
                      px={{ sm: 'spacing-16', md: 'spacing-32', lg: 'spacing-80' }}
                      minWidth={0}
                      backgroundColor="base/elevated"
                      borderRadius={{ lg: 'radius-6xl', md: 'radius-5xl', sm: 'radius-4xl' }}
                      padding={{ sm: 'spacing-20', md: 'spacing-32', lg: 'spacing-32' }}>
                      <Flex
                        size="fullHeight"
                        flexDirection="column"
                        alignItems="center"
                        gap={{ sm: 'spacing-20', md: 'spacing-144', lg: 'spacing-112' }}>
                        <Text
                          textAlign="center"
                          fontStyle={{ sm: 'um_text-xl/bold', md: 'um_text-3xl/bold', lg: 'um_text-3xl/bold' }}
                          color="base/primary"
                          as="div">
                          {data?.carouselCardData?.title}
                        </Text>
                        <Flex
                          flexDirection="column"
                          alignItems="center"
                          justifyContent="space-between"
                          gap={{ sm: 'spacing-20', md: 'spacing-144', lg: 'spacing-64' }}
                          size="fullSize">
                          <Carousel
                            content={getCarouselContent(data?.carouselCardData?.stories ?? [])}
                            showDots={false}
                          />
                          {data?.carouselCardData?.viewMore && (
                            <Button
                              type="solid"
                              size={{ sm: 'block', md: 'md', lg: 'md' }}
                              onClick={() => {
                                void handleViewMoreClick(data?.carouselCardData?.viewMore?.event || {})
                              }}>
                              <Text fontStyle="um_text-base/medium" color="base/inverted primary">
                                {' '}
                                {data?.carouselCardData?.viewMore?.title}{' '}
                              </Text>
                            </Button>
                          )}
                        </Flex>
                      </Flex>
                    </Container>
                  </PropertyControlledComponent>
                </Flex>
              </Reveal>
              <Reveal as="div" animationProps={{ threshold: 0.1, animationDelayMs: 200 }}>
                <TrustHomeVideoPlayers videoPlayerData={data.videoPlayerData} containerType={type} />
              </Reveal>
            </Flex>
          </Flex>
        </Flex>
      </Container>
    </Container>
  )
})
export default Trust
